import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getPatientBasicInfo, getPatientMedicalInfo } from "../../networkCalls";
import {
  PatientMedicalInfoSchema,
  PatientMedicalProfileForm,
} from "../MedicalProfile/PatientMedicalProfileForm";
import { mapPatientMedicalInfoToState } from "../utils";
import { PatientProfileForm } from "./PatientProfileForm";
import { AxiosError } from "axios";
import { convertErrorResponseToMessage, showToastType } from "../../utils";
import {
  patientBasicInfoType,
  patientMedicalInfoEntityType,
} from "./PatientTypes";
import { z } from "zod";
import { PatientPrescription } from "../Prescription/PatientPrescription";
import { PatientHeader } from "./PatientHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../../components/ui/breadcrumb";

var _ = require("lodash");

type ProfileTab = "general" | "medical" | "prescriptions";

export function PatientProfile() {
  const { patientID, appointmentID } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ProfileTab>("general");

  const [patientIdState, setPatientIdState] = useState<string>("");
  const [patientBasicInfoState, setPatientBasicInfoState] =
    useState<patientBasicInfoType>({
      id: null,
      firstName: null,
      lastName: null,
      email: null,
      phoneNumber: null,
      refferedBy: null,
    });
  const [patientMedicalInfoState, setPatientMedicalInfoState] = useState<
    z.infer<typeof PatientMedicalInfoSchema>
  >(mapPatientMedicalInfoToState(null, patientIdState));

  function updatePatientMedicalInfoState(
    patientmedicalInfo: patientMedicalInfoEntityType | null,
    patientId: string
  ) {
    const patientInfo = mapPatientMedicalInfoToState(
      patientmedicalInfo,
      patientId
    );
    setPatientMedicalInfoState(patientInfo);
  }

  function getPatientInfo(patientID: string) {
    getPatientBasicInfo(patientID)
      .then((patientBasicInfo) => {
        if (patientBasicInfo == null) {
          toast.error("Unable to find Patient info. Please refresh the page");
        } else {
          setPatientBasicInfoState({
            id: patientBasicInfo.id,
            firstName: patientBasicInfo.firstName,
            lastName: patientBasicInfo.lastName,
            email: patientBasicInfo.email,
            phoneNumber: patientBasicInfo.phoneNumber,
            refferedBy: patientBasicInfo.refferedBy,
          });
        }
      })
      .catch((error) => {
        // Replace this with Handle Error Function
        console.error(error.message);
        if (error instanceof AxiosError) {
          const addPatientErrorObject = convertErrorResponseToMessage(error);
          const message = addPatientErrorObject["message"];
          const status = addPatientErrorObject["statusCode"];
          showToastType(message, status);
        } else if (
          error.hasOwnProperty("response") &&
          error.response.hasOwnProperty("data") &&
          error.response.data.hasOwnProperty("message")
        ) {
          var errorMessage = error.message;
          errorMessage = error.response.data.message;
          toast.error(errorMessage);
        } else {
          toast.error("Unexpected Error occured. Please try again");
        }
      });
  }
  function getPatientMedInfo(patientId: string) {
    getPatientMedicalInfo(patientId)
      .then((patientMedicalInfo) => {
        if (!_.isEmpty(patientMedicalInfo)) {
          updatePatientMedicalInfoState(patientMedicalInfo, patientId);
        } else {
          updatePatientMedicalInfoState(null, patientId);
        }
      })
      .catch((error) => {
        if (error.response.status === 404) {
          toast.error(error.response.data.message);
        } else {
          toast.error(
            "Unable to find Patient Medical Info. Please refresh the page"
          );
        }
      });
  }
  useEffect(() => {
    if (patientID != null || patientID !== undefined) {
      setPatientIdState(patientID);
      getPatientInfo(patientID);
      getPatientMedInfo(patientID);
    }
  }, [patientID]);

  const patientName =
    [patientBasicInfoState.firstName, patientBasicInfoState.lastName]
      .filter(Boolean)
      .join(" ") || "Patient";

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6">
      {/* Breadcrumb: Board > Patient Name */}
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              className="cursor-pointer"
              onClick={() => navigate("/appointments")}
            >
              Board
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{patientName}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Sticky patient header */}
      <PatientHeader
        patientBasicInfoState={patientBasicInfoState}
        patientMedicalInfoState={patientMedicalInfoState}
        // Best-effort lane status: presence of an appointment in the route
        // means the patient at least has an appointment. Real In-Progress/Done
        // state lives on the appointment entity and is not fetched here yet.
        status={appointmentID ? "HAS_APPOINTMENT" : undefined}
      />

      {/* Tabbed sections */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as ProfileTab)}
      >
        <TabsList className="mb-4 inline-flex gap-1">
          <TabsTrigger
            value="general"
            tabValue={activeTab}
            onValueChange={(v) => setActiveTab(v as ProfileTab)}
          >
            General Info
          </TabsTrigger>
          <TabsTrigger
            value="medical"
            tabValue={activeTab}
            onValueChange={(v) => setActiveTab(v as ProfileTab)}
          >
            Medical
          </TabsTrigger>
          <TabsTrigger
            value="prescriptions"
            tabValue={activeTab}
            onValueChange={(v) => setActiveTab(v as ProfileTab)}
          >
            Prescriptions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" tabValue={activeTab}>
          <PatientProfileForm
            patientId={patientIdState}
            patientBasicInfoState={patientBasicInfoState}
            setPatientBasicInfoState={setPatientBasicInfoState}
          />
        </TabsContent>

        <TabsContent value="medical" tabValue={activeTab}>
          <PatientMedicalProfileForm
            patientMedicalInfoState={patientMedicalInfoState}
            isNew={patientMedicalInfoState.id == null}
            updatePatientMedicalInfoState={updatePatientMedicalInfoState}
            patientIdState={patientIdState}
            patientBasicInfoState={patientBasicInfoState}
          />
        </TabsContent>

        <TabsContent value="prescriptions" tabValue={activeTab}>
          <PatientPrescription
            appointmentId={appointmentID}
            patientBasicInfoState={patientBasicInfoState}
            patientMedicalInfoState={patientMedicalInfoState}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
